import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Sparkles, BookOpen, Clock } from "lucide-react";
import { CategoryIcon } from "@/components/kb/CategoryIcon";

const DIFFICULTY_COLOR = {
  Principiante: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Intermedio: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  Avanzado: "bg-rose-500/10 text-rose-400 border-rose-500/20",
};

export default function KnowledgeHub() {
  const [categories, setCategories] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/kb/categories").then(({ data }) => setCategories(data));
    api.get("/kb/featured").then(({ data }) => setFeatured(data));
  }, []);

  if (!categories) {
    return (
      <div className="space-y-8 max-w-6xl mx-auto">
        <Skeleton className="h-48 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 max-w-6xl mx-auto animate-in-up">
      {/* Hero */}
      <div className="relative rounded-2xl bg-gradient-hero p-8 md:p-12 overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-accent" />
            <span className="text-sm font-medium text-accent">VIA — Valor de la IA</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
            Knowledge Hub
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            Aprende a usar inteligencia artificial de forma segura y productiva.
            Encuentra guías, prompts, buenas prácticas y casos de éxito.
          </p>
          {/* Search */}
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar artículos, prompts, guías..."
              className="pl-10 bg-background/80 border-border/50 backdrop-blur-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && search.trim()) {
                  window.location.href = `/kb/buscar?q=${encodeURIComponent(search)}`;
                }
              }}
            />
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <div className="w-full h-full bg-gradient-to-l from-accent/30 to-transparent" />
        </div>
      </div>

      {/* Categories Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-4 text-foreground">Explorar por categoría</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <Link key={cat.id} to={`/kb/${cat.slug}`}>
              <Card className="h-full border-border/50 bg-card hover:border-accent/40 hover:shadow-float transition-all duration-300 group cursor-pointer">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center mb-3">
                      <CategoryIcon slug={cat.slug} className="h-4.5 w-4.5 text-accent" />
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </div>
                  <h3 className="font-semibold text-sm text-foreground group-hover:text-accent transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {cat.description}
                  </p>
                  <div className="mt-3">
                    <Badge variant="outline" className="text-[10px] bg-muted/50 text-muted-foreground border-border/50">
                      {cat.article_count} artículos
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Featured Articles */}
      {featured.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-accent" />
            Destacados
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.slice(0, 6).map((art) => (
              <Link key={art.id} to={`/kb/article/${art.slug}`}>
                <Card className="h-full border-border/50 bg-card hover:border-accent/30 transition-all duration-200 group">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-sm text-foreground group-hover:text-accent transition-colors line-clamp-2">
                      {art.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{art.summary}</p>
                    <div className="flex items-center gap-2 mt-3">
                      {art.difficulty && (
                        <Badge variant="outline" className={`text-[10px] ${DIFFICULTY_COLOR[art.difficulty]}`}>
                          {art.difficulty}
                        </Badge>
                      )}
                      {art.estimated_minutes && (
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Clock className="h-3 w-3" /> {art.estimated_minutes} min
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
